import MarketWidgetHeader from './MarketWidgetHeader.jsx'
import { projectSetupLifecycle } from '../../lib/positionListStage.js'
import { createPortal } from 'react-dom'
import PositionLedgerPager from './PositionLedgerPager.jsx'
import { useEquityPriceDisplays } from '../../hooks/useEquityPriceDisplays.js'
import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { AlertTriangle } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../hooks/useAuth'
import { parseOCC, isOCCSymbol, formatOCC } from '../../lib/occParser'
import { ibkrQuoteStream, selectPositionQuotePrice } from '../../lib/marketData/ibkrQuoteSubscriptionStore.js'
import { equityHeadline } from '../../lib/marketData/equityHeadline.js'
import { quoteSessionPresentation } from '../../lib/marketData/quoteSessionPresentation.js'
import { getMarketSession } from '../../lib/marketSession'
import AccountName from '../AccountName'
import PrivateValue from '../PrivateValue'
import { derivePositionPnl } from '../../lib/broker/normalizedPnl'
import { computeEquityLotAccounting } from '../../lib/positionLotAccounting'

/* -- Formatters ----------------------------------------------------------- */

const currencyFmt = new Intl.NumberFormat('en-US', {
  style: 'currency', currency: 'USD',
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})

function fmtCurrency(val) {
  if (val == null) return '\u2014'
  return currencyFmt.format(Number(val))
}

function fmtSigned(val) {
  if (val == null) return '\u2014'
  const n = Number(val)
  return (n >= 0 ? '+' : '') + fmtCurrency(n)
}

function fmtLossCurrency(val) {
  if (val == null) return '\u2014'
  const n = Math.abs(Number(val) || 0)
  return '-' + fmtCurrency(n)
}

function fmtPct(val) {
  if (val == null || !isFinite(val)) return '\u2014'
  return (val >= 0 ? '+' : '') + val.toFixed(2) + '%'
}

function positivePrice(value) {
  const numeric = Number(value)
  return Number.isFinite(numeric) && numeric > 0 ? numeric : null
}

function selectHeadlineChangeReference({
  isExtended,
  isOption,
  sessionClose,
  lastRth,
  previousClose,
}) {
  const dailyClose = positivePrice(previousClose)
  if (!isExtended || isOption) return dailyClose
  return positivePrice(sessionClose) ?? positivePrice(lastRth) ?? dailyClose
}

function fmtLossPct(val) {
  if (val == null || !isFinite(val)) return '\u2014'
  return '-' + Math.abs(val).toFixed(2) + '%'
}

function fmtQty(val) {
  if (val == null) return '\u2014'
  const n = Number(val)
  if (n === Math.floor(n)) return n.toLocaleString()
  return n.toLocaleString(undefined, { maximumFractionDigits: 4 })
}

function clr(val) {
  if (val > 0) return 'text-forest'
  if (val < 0) return 'text-ember'
  return 'text-inkMuted'
}

function daysBetween(dateStr) {
  if (!dateStr) return null
  const d = new Date(dateStr)
  const now = new Date()
  return Math.floor((now - d) / 86400000)
}

const METRIC_PLACEHOLDER = '\u2014'
const HEADER_ROW_CLASS = 'flex items-center gap-3 min-h-[34px] min-w-0'
const METRIC_GRID = 'hidden lg:grid grid-cols-[0.7fr_1.35fr_1.45fr_0.75fr_0.8fr_0.7fr] items-end gap-x-4 gap-y-1 min-h-[42px]'
const HOVER_DISMISS_PADDING_PX = 4

function isPointInsideRect(event, rect) {
  return event.clientX >= rect.left - HOVER_DISMISS_PADDING_PX &&
    event.clientX <= rect.right + HOVER_DISMISS_PADDING_PX &&
    event.clientY >= rect.top - HOVER_DISMISS_PADDING_PX &&
    event.clientY <= rect.bottom + HOVER_DISMISS_PADDING_PX
}

function useHoverDropdown({ enabled = true } = {}) {
  const triggerRef = useRef(null)
  const [open, setOpen] = useState(false)

  const close = useCallback(() => setOpen(false), [])
  const openDropdown = useCallback(() => {
    if (enabled) setOpen(true)
  }, [enabled])
  const toggle = useCallback(() => {
    if (enabled) setOpen(prev => !prev)
  }, [enabled])

  useEffect(() => {
    if (!enabled) close()
  }, [close, enabled])

  useEffect(() => {
    if (!open) return

    const handleMove = (event) => {
      const trigger = triggerRef.current
      if (!trigger) {
        close()
        return
      }
      if (!isPointInsideRect(event, trigger.getBoundingClientRect())) close()
    }
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') close()
    }

    window.addEventListener('pointermove', handleMove, true)
    window.addEventListener('mousemove', handleMove, true)
    window.addEventListener('scroll', close, true)
    window.addEventListener('blur', close)
    window.addEventListener('keydown', handleKeyDown)

    return () => {
      window.removeEventListener('pointermove', handleMove, true)
      window.removeEventListener('mousemove', handleMove, true)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('blur', close)
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [close, open])

  return {
    open,
    triggerRef,
    openDropdown,
    close,
    toggle,
  }
}

function isMobileHeaderViewport() {
  if (typeof window === 'undefined' || !window.matchMedia) return false
  return window.matchMedia('(max-width: 1023px)').matches
}

/* -- Component ------------------------------------------------------------ */

export default function PositionHeader({
  projects,
  trades,
  executions,
  staticData,
  isGrouped,
  // EF-114: setups moved to a dedicated tab in the right panel; the header's
  // right box now shows a status/alerts strip driven by `protection`.
  protection,
  wsOrdersReady,
  liveStopOrders = null,
  brokerRefreshVersion = 0,
  onPlaceStop,
  // EF-134e: lift the live currentPrice up to PositionWorkspace so ActionPanel
  // can show a "BE above market" warning. Fired from a useEffect that watches
  // data?.currentPrice — null while data isn't ready.
  onPriceUpdate,
  onQuoteUpdate,
  onAccountingUpdate,
  quoteSnapshot,
  positionList = false,
  ledger = false,
  ledgerTarget = null,
}) {
  const primary = projectSetupLifecycle(projects[0]) || null
  const { user } = useAuth()

  const stage = (primary?.stage || 'idea').toLowerCase()
  const isActive = stage === 'active'
  const isClosed = stage === 'closed' || stage === 'reviewed'
  const symbol = primary?.symbol || '---'
  const emptyWorkspace = primary?.id == null && !primary?.trade_id && !primary?.__workspaceShell && stage === 'idea'
  const stageLabel = emptyWorkspace ? 'No setup' : stage.charAt(0).toUpperCase() + stage.slice(1)
  const workspaceStatusLabel = emptyWorkspace ? 'No current setup' : `${stageLabel} workspace`
  const equityDisplay = useEquityPriceDisplays([{ symbol }])[symbol.toUpperCase()]

  /* -- Live position data (schwab_positions) for active positions -- */
  const [positions, setPositions] = useState([])
  const [tradeStops, setTradeStops] = useState([])
  const [accountEquities, setAccountEquities] = useState([])
  const [loading, setLoading] = useState(isActive)
  const liveDataRequestRef = useRef(0)

  const loadLiveData = useCallback(async ({ resetShell = false } = {}) => {
    if (!user || !symbol || !isActive) return
    const requestId = liveDataRequestRef.current + 1
    liveDataRequestRef.current = requestId
    // EF-191: clear stale positions/tradeStops from the previous symbol BEFORE
    // the supabase await. Without this, the `live` useMemo computes schwabPrice
    // from the old symbol's market_value/qty during the 100-500ms query window.
    // With this clear, the `if (!isActive || positions.length === 0 || positions.some(position => position.symbol !== symbol)) return null`
    // guard at the live useMemo suppresses the entire P&L block during the gap,
    // so the header renders a clean loading state instead of mixed-symbol data.
    if (resetShell) {
      setPositions([])
      setTradeStops([])
      setAccountEquities([])
      setLoading(true)
    }
    const [{ data: pos }, { data: stops }, { data: accounts }] = await Promise.all([
      supabase
        .from('schwab_positions')
        .select('account_number, symbol, instrument_type, quantity, average_price, market_value, current_day_pnl, prev_close_price, raw, status, cost_basis_long, cost_basis_short, unrealized_pnl_long, unrealized_pnl_short, cost_basis_source, currency, contract_multiplier, last_updated_at')
        .eq('user_id', user.id)
        .eq('symbol', symbol)
        .order('market_value', { ascending: false }),
      supabase
        .from('trades')
        .select('account_number, stop_loss, initial_stop, risk_amount, entry_date')
        .eq('user_id', user.id)
        .eq('symbol', symbol)
        .in('status', ['open', 'partial'])
        .not('stop_loss', 'is', null),
      supabase
        .from('schwab_accounts')
        .select('account_number, equity')
        .eq('user_id', user.id)
        .order('account_number'),
    ])
    if (requestId !== liveDataRequestRef.current) return
    setPositions((pos || []).filter(p => p.status === 'open' || !p.status))
    setTradeStops(stops || [])
    setAccountEquities(accounts || [])
    setLoading(false)
  }, [user, symbol, isActive])

  useEffect(() => { loadLiveData({ resetShell: true }) }, [loadLiveData])

  useEffect(() => {
    if (!brokerRefreshVersion) return
    loadLiveData({ resetShell: false })
  }, [brokerRefreshVersion, loadLiveData])

  /* -- IBKR live price via WebSocket stream -- */
  const [ibkrPrice, setIbkrPrice] = useState(null)
  const [ibkrClose, setIbkrClose] = useState(null)
  const [ibkrSessionClose, setIbkrSessionClose] = useState(null)
  const [ibkrLastRth, setIbkrLastRth] = useState(null)
  const [ibkrBid, setIbkrBid] = useState(null)
  const [ibkrAsk, setIbkrAsk] = useState(null)
  const [ibkrMark, setIbkrMark] = useState(null)
  const [ibkrQuoteSession, setIbkrQuoteSession] = useState(null)
  const [ibkrContract, setIbkrContract] = useState(null)
  const hasControlledQuote = quoteSnapshot !== undefined

  useEffect(() => {
    if (hasControlledQuote) return undefined

    // EF-191: clear last-tick state on symbol change. Without this, a stale
    // value from the previous symbol's subscription persists in state until
    // the new symbol delivers its first tick. On weekends or in low-tick
    // conditions the first tick may not arrive for hours, leading to phantom
    // P&L (e.g., SOLS cost basis × TSLA's stale $418.57 last). Nulling these
    // forces the `live` useMemo's fallback chain to fall through to
    // schwabPrice (computed from fresh positions[]) instead.
    setIbkrPrice(null)
    setIbkrClose(null)
    setIbkrSessionClose(null)
    setIbkrLastRth(null)
    setIbkrBid(null)
    setIbkrAsk(null)
    setIbkrMark(null)
    setIbkrQuoteSession(null)
    setIbkrContract(null)

    // EF-159c: subscribe for any rendered symbol regardless of stage.
    // The non-active header (ephemeral / IDEA / WORKING) needs the same
    // live tick stream as ACTIVE to render price + day-change.
    if (!symbol || symbol === '---') return
    let unsub = null

    const isOpt = isOCCSymbol(symbol)
    const occ = isOpt ? parseOCC(symbol) : null
    const streamSymbol = occ ? occ.underlying : symbol
    const params = occ
      ? { secType: 'OPT', expiry: occ.expiry, strike: String(occ.strike), right: occ.right }
      : {}

    ibkrQuoteStream.subscribe(streamSymbol, (tick) => {
      if (tick.field === 'reset') {
        setIbkrPrice(null)
        setIbkrClose(null)
        setIbkrSessionClose(null)
        setIbkrLastRth(null)
        setIbkrBid(null)
        setIbkrAsk(null)
        setIbkrMark(null)
        setIbkrQuoteSession(null)
        setIbkrContract(null)
        return
      }
      if (tick.session) setIbkrQuoteSession(tick.session)
      if (tick.contract) setIbkrContract(tick.contract)
      if (tick.field === 'last' && tick.value > 0) setIbkrPrice(tick.value)
      if (tick.field === 'close' && tick.value > 0) setIbkrClose(tick.value)
      if (tick.field === 'sessionClose' && tick.value > 0) setIbkrSessionClose(tick.value)
      if (tick.field === 'lastRth' && tick.value > 0) setIbkrLastRth(tick.value)
      if (tick.field === 'bid' && tick.value > 0) setIbkrBid(tick.value)
      if (tick.field === 'ask' && tick.value > 0) setIbkrAsk(tick.value)
      if (tick.field === 'mark' && tick.value > 0) setIbkrMark(tick.value)
    }, params).then(fn => { unsub = fn }).catch(() => {})

    return () => { if (unsub) unsub() }
  }, [symbol, hasControlledQuote])

  const effectiveIbkrPrice = hasControlledQuote ? (Number(quoteSnapshot?.last) || null) : ibkrPrice
  const effectiveIbkrClose = hasControlledQuote ? (Number(quoteSnapshot?.close) || null) : ibkrClose
  const effectiveIbkrSessionClose = hasControlledQuote ? (Number(quoteSnapshot?.sessionClose) || null) : ibkrSessionClose
  const effectiveIbkrLastRth = hasControlledQuote ? (Number(quoteSnapshot?.lastRth) || null) : ibkrLastRth
  const effectiveIbkrBid = hasControlledQuote ? (Number(quoteSnapshot?.bid) || null) : ibkrBid
  const effectiveIbkrAsk = hasControlledQuote ? (Number(quoteSnapshot?.ask) || null) : ibkrAsk
  const effectiveIbkrMark = hasControlledQuote ? (Number(quoteSnapshot?.mark) || null) : ibkrMark
  const effectiveIbkrQuoteSession = hasControlledQuote ? (quoteSnapshot?.session || null) : ibkrQuoteSession
  const effectiveIbkrContract = hasControlledQuote ? (quoteSnapshot?.contract || null) : ibkrContract

  /* -- Market session (60s poll) -- */
  const [marketSession, setMarketSession] = useState(() => getMarketSession())

  useEffect(() => {
    const interval = setInterval(() => {
      const s = getMarketSession()
      setMarketSession(prev => prev !== s ? s : prev)
    }, 60000)
    return () => clearInterval(interval)
  }, [])

  /* -- Build stop map -- */
  const stopMap = useMemo(() => {
    const map = {}
    for (const t of tradeStops) {
      map[`${symbol}|${t.account_number}`] = {
        stopPrice: Number(t.stop_loss),
        initialStop: t.initial_stop != null ? Number(t.initial_stop) : null,
        riskAmount: t.risk_amount != null ? Number(t.risk_amount) : null,
        entryDate: t.entry_date,
      }
    }
    return map
  }, [tradeStops, symbol])

  const portfolioEquity = useMemo(() => {
    return accountEquities.reduce((sum, account) => {
      const eq = account.equity
      const value = typeof eq === 'number'
        ? eq
        : (eq?.equity ?? eq?.liquidationValue ?? eq?.totalAccountValue ?? 0)
      return sum + (Number(value) || 0)
    }, 0)
  }, [accountEquities])

  /* -- Consolidated live data -- */
  const live = useMemo(() => {
    if (!isActive || positions.length === 0 || positions.some(position => position.symbol !== symbol)) return null

    let totalQty = 0, totalMktVal = 0, totalCostBasis = 0, totalDayPnl = 0, totalAverageEntryValue = 0
    let totalBrokerOpenPnl = 0, hasBrokerOpenPnl = false
    const accounts = []

    for (const pos of positions) {
      const qty = Number(pos.quantity) || 0
      const mktVal = Number(pos.market_value) || 0
      const dayPnl = Number(pos.current_day_pnl) || 0
      const mul = Number(pos?.contract_multiplier)
        || (pos?.instrument_type === 'OPTION' ? 100 : 1)
      // EF-156: Single source of truth for cost basis. Reads normalized row.
      const { costBasis, gainLoss, pnlSource } = derivePositionPnl(pos)
      totalQty += qty
      totalMktVal += mktVal
      totalCostBasis += costBasis
      totalDayPnl += dayPnl
      totalAverageEntryValue += (Number(pos.average_price) || 0) * Math.abs(qty) * mul
      if (pnlSource === 'broker_open_pnl') {
        totalBrokerOpenPnl += gainLoss
        hasBrokerOpenPnl = true
      }

      const sm = stopMap[`${pos.symbol}|${pos.account_number}`]
      const acctLiveStops = Array.isArray(liveStopOrders)
        ? liveStopOrders.filter(s => String(s.accountNumber || '') === String(pos.account_number || ''))
        : []
      const liveStopPrice = acctLiveStops.length === 1 && acctLiveStops[0]?.stopPrice != null
        ? Number(acctLiveStops[0].stopPrice)
        : null
      const dbStopFallback = wsOrdersReady ? null : sm?.stopPrice
      accounts.push({
        accountNumber: pos.account_number,
        qty,
        // Allocation is measured against this account's equity, not symbol quantity.
        allocationPct: (() => {
          const equity = accountEquities.find(account => String(account.account_number) === String(pos.account_number))?.equity
          const value = (typeof equity === 'number' || typeof equity === 'string') ? equity : equity?.equity ?? equity?.liquidationValue ?? equity?.totalAccountValue
          return pos.market_value != null && Number.isFinite(Number(pos.market_value)) && Number(value) > 0
            ? Number(pos.market_value) / Number(value) * 100 : null
        })(),
        stopPrice: liveStopPrice ?? dbStopFallback ?? null,
        riskAmount: sm?.riskAmount ?? null,
        entryDate: sm?.entryDate ?? null,
      })
    }

    // Derive per-share price from Schwab market value
    // For options: market_value is already ×100, so divide by qty AND 100 to get per-share price
    // For equities: market_value / qty = per-share price
    const isOpt = positions[0]?.instrument_type === 'OPTION'
    const optMul = isOpt ? 100 : 1
    const schwabPrice = totalQty > 0 ? totalMktVal / (totalQty * optMul) : 0
    const livePrice = selectPositionQuotePrice(
      { last: effectiveIbkrPrice, close: effectiveIbkrClose, sessionClose: effectiveIbkrSessionClose, lastRth: effectiveIbkrLastRth, bid: effectiveIbkrBid, ask: effectiveIbkrAsk, contract: effectiveIbkrContract },
      effectiveIbkrQuoteSession || marketSession,
      isOpt ? 'OPT' : 'STK',
    ) ?? schwabPrice

    // Recompute market value if IBKR live price is available
    const liveMktVal = livePrice > 0 ? livePrice * totalQty * optMul : totalMktVal
    const lotAccounting = !isOpt
      ? computeEquityLotAccounting(executions, {
          symbol,
          accountNumbers: accounts.map(a => a.accountNumber),
          markPrice: livePrice,
          method: 'LIFO',
        })
      : null
    const lotQtyMatchesBroker = lotAccounting?.available
      && lotAccounting.unmatchedExitQty === 0
      && Math.abs(Number(lotAccounting.remainingQty) - Math.abs(totalQty)) < 0.0001
    const usesLotAccounting = Boolean(lotQtyMatchesBroker)

    if (usesLotAccounting) {
      totalCostBasis = lotAccounting.remainingBasis
    }

    const openPnl = usesLotAccounting ? lotAccounting.openPnl : liveMktVal - totalCostBasis
    const campaignPnl = usesLotAccounting ? lotAccounting.campaignPnl : openPnl
    const unrealizedPnl = campaignPnl
    const unrealizedPct = totalCostBasis > 0 ? (unrealizedPnl / totalCostBasis) * 100 : 0
    const openPnlPct = totalCostBasis > 0 && openPnl != null ? (openPnl / totalCostBasis) * 100 : null
    const avgEntryPrice = usesLotAccounting && lotAccounting.remainingAvgPrice > 0
      ? lotAccounting.remainingAvgPrice
      : (totalQty > 0 ? totalAverageEntryValue / (Math.abs(totalQty) * optMul) : 0)
    const profitable = campaignPnl >= 0

    const brokerStops = Array.isArray(liveStopOrders)
      ? liveStopOrders
          .map(s => ({
            accountNumber: s.accountNumber,
            stopPrice: Number(s.stopPrice),
            quantity: Math.abs(Number(s.quantity) || 0),
          }))
          .filter(s => Number.isFinite(s.stopPrice) && s.stopPrice > 0)
      : []
    const fallbackStops = accounts
      .filter(a => a.stopPrice != null && a.qty > 0)
      .map(a => ({
        accountNumber: a.accountNumber,
        stopPrice: Number(a.stopPrice),
        quantity: Math.abs(Number(a.qty) || 0),
      }))
    const activeStops = brokerStops.length > 0 ? brokerStops : fallbackStops
    const firstStop = activeStops[0] || null
    const effectiveStop = firstStop?.stopPrice ?? null
    const hasStopCoverage = activeStops.length > 0
    const exposureByAccount = accounts.reduce((acc, account) => {
      const accountNumber = String(account.accountNumber || '')
      const heldQty = Math.abs(Number(account.qty) || 0)
      const accountStops = activeStops.filter(s => String(s.accountNumber || '') === accountNumber)
      let protectedQty = 0
      let stopNet = 0
      let openNet = 0

      for (const stop of accountStops) {
        const remainingQty = Math.max(0, heldQty - protectedQty)
        const stopQty = Math.min(
          remainingQty,
          Math.abs(Number(stop.quantity) || remainingQty),
        )
        if (stopQty <= 0) continue
        protectedQty += stopQty
        stopNet += (stop.stopPrice - avgEntryPrice) * stopQty * (isOpt ? 100 : 1)
        openNet += (stop.stopPrice - livePrice) * stopQty * (isOpt ? 100 : 1)
      }

      const unprotectedQty = Math.max(0, heldQty - protectedQty)
      stopNet += (0 - avgEntryPrice) * unprotectedQty * (isOpt ? 100 : 1)
      openNet += (0 - livePrice) * unprotectedQty * (isOpt ? 100 : 1)

      return {
        stopNet: acc.stopNet + stopNet,
        openNet: acc.openNet + openNet,
        protectedQty: acc.protectedQty + protectedQty,
        unprotectedQty: acc.unprotectedQty + unprotectedQty,
      }
    }, { stopNet: 0, openNet: 0, protectedQty: 0, unprotectedQty: 0 })
    const openPositionRisk = Math.abs(exposureByAccount.openNet) >= 0.005 ? exposureByAccount.openNet : 0
    const openPositionRiskPct = liveMktVal > 0 ? (openPositionRisk / liveMktVal) * 100 : null
    const currentRisk = Math.abs(Math.min(0, exposureByAccount.stopNet)) < 0.005
      ? 0
      : Math.abs(Math.min(0, exposureByAccount.stopNet))
    const lockedProfit = Math.abs(Math.max(0, exposureByAccount.stopNet)) < 0.005
      ? 0
      : Math.max(0, exposureByAccount.stopNet)
    const initialRisk = accounts.reduce((s, a) => s + (a.riskAmount || 0), 0) || null
    const totalRisk = hasStopCoverage || wsOrdersReady ? currentRisk : initialRisk
    const stopNetExposure = hasStopCoverage || wsOrdersReady
      ? exposureByAccount.stopNet
      : (totalRisk != null ? -totalRisk : null)
    const stopExposurePct = totalCostBasis > 0 && stopNetExposure != null
      ? (stopNetExposure / totalCostBasis) * 100
      : null
    const startingCapitalRiskPct = totalCostBasis > 0 && initialRisk != null
      ? (initialRisk / totalCostBasis) * 100
      : null
    const openPortfolioRiskPct = portfolioEquity > 0 ? (openPositionRisk / portfolioEquity) * 100 : null
    const portfolioStartingRiskPct = portfolioEquity > 0 && initialRisk != null
      ? (initialRisk / portfolioEquity) * 100
      : null
    const rRisk = initialRisk ?? (currentRisk > 0 ? currentRisk : null)
    const rMultiple = rRisk && rRisk > 0 ? campaignPnl / rRisk : null

    const entryDates = accounts.map(a => a.entryDate).filter(Boolean)
    const earliestEntry = entryDates.length > 0 ? entryDates.sort()[0] : null
    const daysInTrade = daysBetween(earliestEntry)

    const quoteSession = effectiveIbkrQuoteSession || marketSession
    const sessionPresentation = quoteSessionPresentation(quoteSession)
    const isExtended = sessionPresentation != null

    // Day change: prefer IBKR close, then our stored prev_close_price (set daily by sync)
    const hasIbkrClose = effectiveIbkrClose != null && effectiveIbkrClose > 0
    // prev_close_price is set once per day by the Schwab sync — reliable for all instruments
    const storedClose = positions.reduce((best, p) => {
      const cp = Number(p.prev_close_price)
      return cp > 0 ? cp : best
    }, 0)
    const effectiveClose = isOpt
      ? (storedClose > 0 ? storedClose : (hasIbkrClose ? effectiveIbkrClose : null))
      : (hasIbkrClose ? effectiveIbkrClose : (storedClose > 0 ? storedClose : null))
    const headlineChangeReference = selectHeadlineChangeReference({
      isExtended,
      isOption: isOpt,
      sessionClose: effectiveIbkrSessionClose,
      lastRth: effectiveIbkrLastRth,
      previousClose: effectiveClose,
    })
    const closePrice = isExtended && !isOpt
      ? headlineChangeReference
      : (effectiveIbkrClose ?? schwabPrice)
    const hasExtendedMove = isExtended && headlineChangeReference != null && Math.abs(livePrice - headlineChangeReference) > 0.005
    const dayChangeDollar = headlineChangeReference
      ? livePrice - headlineChangeReference
      : 0
    const dayChangePct = headlineChangeReference
      ? ((livePrice - headlineChangeReference) / headlineChangeReference) * 100
      : 0
    const hasDayChange = headlineChangeReference != null

    return {
      currentPrice: livePrice, totalQty, totalMktVal: liveMktVal, totalCostBasis, avgEntryPrice,
      unrealizedPnl, unrealizedPct, totalDayPnl,
      usesLotAccounting, campaignPnl, openPnl, openPnlPct,
      brokerOpenPnl: hasBrokerOpenPnl ? totalBrokerOpenPnl : null,
      realizedPnl: usesLotAccounting ? lotAccounting.realizedPnl : null,
      effectiveStop, totalRisk, lockedProfit, stopNetExposure, stopExposurePct,
      openPositionRisk, openPositionRiskPct, initialRisk, startingCapitalRiskPct,
      openPortfolioRiskPct, portfolioStartingRiskPct, rMultiple, daysInTrade,
      profitable, accounts, session: quoteSession, isExtended,
      closePrice, hasExtendedMove, dayChangeBaseline: headlineChangeReference,
      dayChangeDollar, dayChangePct, hasDayChange,
    }
  }, [isActive, positions, stopMap, wsOrdersReady, effectiveIbkrPrice, effectiveIbkrClose, effectiveIbkrSessionClose, effectiveIbkrLastRth, effectiveIbkrBid, effectiveIbkrAsk, effectiveIbkrQuoteSession, effectiveIbkrContract, marketSession, executions, symbol, liveStopOrders, portfolioEquity, accountEquities])

  const nonActiveCurrentPrice = useMemo(() => {
    if (isActive) return null
    return selectPositionQuotePrice(
      { last: effectiveIbkrPrice, close: effectiveIbkrClose, sessionClose: effectiveIbkrSessionClose, lastRth: effectiveIbkrLastRth, bid: effectiveIbkrBid, ask: effectiveIbkrAsk, contract: effectiveIbkrContract },
      effectiveIbkrQuoteSession || marketSession,
      isOCCSymbol(symbol) ? 'OPT' : 'STK',
    )
  }, [isActive, effectiveIbkrPrice, effectiveIbkrClose, effectiveIbkrSessionClose, effectiveIbkrLastRth, effectiveIbkrBid, effectiveIbkrAsk, effectiveIbkrQuoteSession, effectiveIbkrContract, marketSession, symbol])

  // EF-134e/EF-159c: lift the same quote shown in the header. Active
  // positions publish live.currentPrice; IDEA/WORKING setup workspaces publish
  // the non-active IBKR quote so staging can choose Pullback vs On Strength.
  useEffect(() => {
    if (onPriceUpdate) onPriceUpdate(live?.currentPrice ?? nonActiveCurrentPrice ?? null)
  }, [live?.currentPrice, nonActiveCurrentPrice, onPriceUpdate])

  useEffect(() => {
    if (!onQuoteUpdate) return
    const price = live?.currentPrice ?? nonActiveCurrentPrice ?? null
    onQuoteUpdate(price != null && price > 0
      ? { price, session: effectiveIbkrQuoteSession || marketSession }
      : null)
  }, [live?.currentPrice, nonActiveCurrentPrice, effectiveIbkrQuoteSession, marketSession, onQuoteUpdate])

  useEffect(() => {
    if (onAccountingUpdate) {
      onAccountingUpdate(live?.usesLotAccounting ? {
        remainingAvgPrice: live.avgEntryPrice,
        remainingBasis: live.totalCostBasis,
        campaignPnl: live.campaignPnl,
        openPnl: live.openPnl,
        realizedPnl: live.realizedPnl,
      } : null)
    }
  }, [
    live?.usesLotAccounting,
    live?.avgEntryPrice,
    live?.totalCostBasis,
    live?.campaignPnl,
    live?.openPnl,
    live?.realizedPnl,
    onAccountingUpdate,
  ])

  /* -- Closed trade consolidated data -- */
  const closed = useMemo(() => {
    if (!isClosed || trades.length === 0) return null

    let totalNetPnl = 0, totalRisk = 0, totalQty = 0
    let totalEntryWeighted = 0, totalExitWeighted = 0
    let earliestEntry = null, latestExit = null, maxDuration = null

    for (const t of trades) {
      const qty = Number(t.quantity_total) || 0
      totalQty += qty
      totalEntryWeighted += (Number(t.avg_entry_price) || 0) * qty
      totalExitWeighted += (Number(t.avg_exit_price) || 0) * qty
      totalNetPnl += Number(t.net_pnl) || 0
      totalRisk += Number(t.risk_amount) || 0
      const dur = Number(t.duration_minutes) || 0
      if (dur > 0 && (maxDuration == null || dur > maxDuration)) maxDuration = dur
      if (t.entry_date) {
        const ed = new Date(t.entry_date)
        if (!earliestEntry || ed < earliestEntry) earliestEntry = ed
      }
      if (t.exit_date) {
        const xd = new Date(t.exit_date)
        if (!latestExit || xd > latestExit) latestExit = xd
      }
    }

    const avgEntry = totalQty > 0 ? totalEntryWeighted / totalQty : null
    const avgExit = totalQty > 0 ? totalExitWeighted / totalQty : null
    const pnlPct = avgEntry > 0 ? ((avgExit - avgEntry) / avgEntry) * 100 : null
    const rMultiple = totalRisk > 0 ? totalNetPnl / totalRisk : null
    const stopPrices = trades.map(t => Number(t.stop_loss)).filter(v => v && isFinite(v))
    const daysInTrade = earliestEntry && latestExit
      ? Math.floor((latestExit - earliestEntry) / 86400000)
      : null

    return {
      avgEntry, avgExit, totalNetPnl, totalRisk, pnlPct,
      rMultiple, daysInTrade, totalQty,
      stopPrice: stopPrices[0] ?? null,
      result: totalNetPnl > 0.5 ? 'WIN' : totalNetPnl < -0.5 ? 'LOSS' : 'BE',
      accounts: trades,
    }
  }, [isClosed, trades])

  if (!primary) return null

  /* -- Stage badge classes -- */
  const stageBadge = {
    idea: 'bg-blue-500/10 text-blue-400 border-blue-400/30',
    working: 'bg-[#c4953a]/10 text-[#c4953a] border-[#c4953a]/30',
    planned: 'bg-ink/10 text-ink border-ink/20',
    active: 'bg-forest/10 text-forest border-forest/20',
    closed: 'bg-canvas2 text-inkMuted border-border',
    reviewed: 'bg-canvas2 text-inkMuted border-border',
  }

  const badgeCls = stageBadge[stage] || stageBadge.idea

  /* -- Determine side from executions, setup direction, or default LONG -- */
  const side = useMemo(() => {
    if (primary?.direction) return primary.direction
    if (executions.length > 0) {
      const firstEntry = executions.find(e => e.execution_type === 'entry')
      if (firstEntry) return firstEntry.side === 'sell' ? 'SHORT' : 'LONG'
    }
    return 'LONG'
  }, [executions, primary])

  /* -- Setup metadata (AGI tracker) -- */
  const hasSetup = !!primary?.setup_pattern
  const [mobileMetricsOpen, setMobileMetricsOpen] = useState(false)
  const [isMobileHeaderLayout, setIsMobileHeaderLayout] = useState(isMobileHeaderViewport)
  const sharesBreakdown = useHoverDropdown({ enabled: live?.accounts?.length > 0 })
  const pnlBreakdown = useHoverDropdown({ enabled: !!live })
  const riskBreakdown = useHoverDropdown({ enabled: live?.totalRisk != null })

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined
    const query = window.matchMedia('(max-width: 1023px)')
    const update = () => {
      setIsMobileHeaderLayout(query.matches)
      if (!query.matches) setMobileMetricsOpen(false)
    }
    update()
    query.addEventListener?.('change', update)
    query.addListener?.(update)
    return () => {
      query.removeEventListener?.('change', update)
      query.removeListener?.(update)
    }
  }, [])

  const metricGrid = positionList ? 'grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-x-4 gap-y-1.5 min-w-0' : METRIC_GRID

  /* -- Render helpers -- */
  const Metric = ({ label, children, colorClass, testId }) => (
    <div data-testid={testId} role={positionList ? 'listitem' : undefined} className={positionList ? 'flex items-baseline justify-between gap-3 min-w-0' : 'min-w-0 overflow-hidden'}>
      <div className={positionList ? 'font-mono text-xs text-inkMuted' : 'font-mono text-[10px] tracking-wider uppercase text-inkMuted mb-0.5 truncate'}>{label}</div>
      <div className={`font-mono text-sm font-medium tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-h-[20px] ${colorClass || 'text-ink'}`}>
        {children ?? METRIC_PLACEHOLDER}
      </div>
    </div>
  )

  const MetricSkeleton = ({ testId }) => (
    <div data-testid={testId} className="min-w-0 overflow-hidden" aria-hidden="true">
      <div className="h-3 w-20 rounded bg-canvas2/80 mb-1" />
      <div className="h-5 w-24 rounded bg-canvas2/80 animate-pulse" />
    </div>
  )

  const QuoteRow = ({ sessionLabel, sessionTextClass, price, changeText, closeText, bidText, askText, isUp = true, isExtended = false }) => (
    <div
      data-preserve-color
      data-testid="position-header-quote"
      className="flex items-baseline gap-2 min-w-0 max-w-full"
    >
      {sessionLabel && (
        <span className={`text-[9px] font-mono font-bold uppercase tracking-widest shrink-0 ${sessionTextClass || 'text-blue-400'}`}>
          {sessionLabel}
        </span>
      )}
      <span className={`font-mono text-lg font-bold tabular-nums whitespace-nowrap overflow-hidden text-ellipsis text-left ${
        isExtended ? sessionTextClass : (isUp ? 'text-forest' : 'text-ember')
      }`}>
        {price || METRIC_PLACEHOLDER}
      </span>
      <span className={`font-mono text-xs tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-w-[120px] ${
        isExtended ? sessionTextClass : (isUp ? 'text-forest' : 'text-ember')
      }`}>
        {changeText || ''}
      </span>
      <span className="font-mono text-xs text-inkMuted tabular-nums whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
        {closeText || ''}
      </span>
      {(bidText || askText) && (
        <span className="flex items-baseline gap-2 font-mono text-[10px] text-inkMuted tabular-nums whitespace-nowrap shrink-0">
          {bidText && <span>{bidText}</span>}
          {askText && <span>{askText}</span>}
        </span>
      )}
    </div>
  )

  const MobileInlineQuote = ({ sessionLabel, sessionTextClass, price, changeText, bidText, askText, isUp = true, isExtended = false }) => (
    <div data-preserve-color data-testid="position-header-mobile-inline-quote" className="lg:hidden min-w-0 flex flex-col justify-center">
      {sessionLabel && (
        <span className={`font-mono text-[7px] font-bold uppercase tracking-widest leading-tight ${sessionTextClass || 'text-blue-400'}`}>
          {sessionLabel}
        </span>
      )}
      <span className={`font-mono text-sm font-bold tabular-nums leading-none whitespace-nowrap overflow-hidden text-ellipsis ${
        isExtended ? sessionTextClass : (isUp ? 'text-forest' : 'text-ember')
      }`}>
        {price || METRIC_PLACEHOLDER}
      </span>
      {changeText && (
        <span className={`font-mono text-[9px] tabular-nums leading-tight whitespace-nowrap overflow-hidden text-ellipsis ${
          isExtended ? sessionTextClass : (isUp ? 'text-forest' : 'text-ember')
        }`}>
          {changeText}
        </span>
      )}
      {(bidText || askText) && (
        <span className="flex gap-1.5 font-mono text-[7px] text-inkMuted tabular-nums leading-tight whitespace-nowrap overflow-hidden text-ellipsis">
          {bidText && <span>{bidText}</span>}
          {askText && <span>{askText}</span>}
        </span>
      )}
    </div>
  )

  const MobileMetric = ({ label, children, colorClass, testId, valueClassName }) => (
    <div data-testid={testId} className="min-w-0 rounded-lg border border-border bg-canvas2/40 px-2 py-1">
      <div className="font-mono text-[8px] tracking-wider uppercase text-inkMuted mb-0.5 truncate">{label}</div>
      <div className={valueClassName || `font-mono text-xs font-medium tabular-nums whitespace-nowrap overflow-hidden text-ellipsis ${colorClass || 'text-ink'}`}>
        {children ?? METRIC_PLACEHOLDER}
      </div>
    </div>
  )

  /* -- Account pills helper -- */
  const AccountPills = ({ accounts, qtyKey = 'qty', qtyFmt = fmtQty }) => (
    <div className="flex items-center gap-2 ml-auto shrink-0">
      {accounts.map((a, i) => (
        <span
          key={i}
          className="font-mono text-[11px] py-0.5 px-2 rounded-full bg-canvas2 border border-border text-inkMuted whitespace-nowrap"
        >
          <AccountName accountNumber={a.accountNumber || a.account_number} className="font-mono text-[9px] text-inkMuted" />
          {' '}({qtyFmt(a[qtyKey])})
        </span>
      ))}
    </div>
  )

  /* -- Setup Cards (right half of split header) -- */
  const fmtSetupDate = (src) => {
    if (!src) return null
    const match = src.match(/(\d{4})-(\d{2})-(\d{2})/)
    if (match) { const [, y, m, d] = match; return `${parseInt(m)}/${parseInt(d)}/${y.slice(2)}` }
    return src
  }

  // EF-114: SetupCards component removed \u2014 setups now live in the right
  // panel's "Setups" tab. The header's right box renders a status/alerts
  // strip in its place (see render below).

  const displaySymbol = isOCCSymbol(symbol) ? formatOCC(symbol) : symbol
  const isOptionSymbol = isOCCSymbol(symbol)
  const optionQuoteSession = effectiveIbkrQuoteSession || live?.session || marketSession
  const isOptionMarketOpen = isOptionSymbol && optionQuoteSession === 'regular'
  const hasOptionBidAsk = isOptionMarketOpen && effectiveIbkrBid > 0 && effectiveIbkrAsk > 0
  const optionMidpoint = hasOptionBidAsk
    ? (effectiveIbkrBid + effectiveIbkrAsk) / 2
    : null
  const optionHeadlinePrice = isOptionSymbol
    ? (isOptionMarketOpen
        ? (optionMidpoint ?? effectiveIbkrMark ?? live?.currentPrice ?? nonActiveCurrentPrice)
        : (effectiveIbkrLastRth ?? effectiveIbkrPrice ?? effectiveIbkrSessionClose ?? effectiveIbkrClose ?? live?.currentPrice ?? nonActiveCurrentPrice))
    : null
  const optionBidText = hasOptionBidAsk ? `Bid ${fmtCurrency(effectiveIbkrBid)}` : ''
  const optionAskText = hasOptionBidAsk ? `Ask ${fmtCurrency(effectiveIbkrAsk)}` : ''
  const activePresentation = quoteSessionPresentation(live?.session)
  const activeQuote = isActive && live ? (() => {
    const displayPrice = optionHeadlinePrice ?? live.currentPrice
    const changeBaseline = isOptionSymbol ? live.dayChangeBaseline : null
    const dayChangeDollar = isOptionSymbol && changeBaseline > 0
      ? displayPrice - changeBaseline
      : live.dayChangeDollar
    const dayChangePct = isOptionSymbol && changeBaseline > 0
      ? (dayChangeDollar / changeBaseline) * 100
      : live.dayChangePct
    const hasDayChange = isOptionSymbol ? changeBaseline > 0 : live.hasDayChange
    return {
      sessionLabel: activePresentation?.label || '',
      sessionTextClass: activePresentation?.textClass || '',
      price: fmtCurrency(displayPrice),
      changeText: hasDayChange
        ? `${fmtSigned(dayChangeDollar)} (${dayChangePct >= 0 ? '+' : ''}${dayChangePct.toFixed(2)}%)`
        : '',
      closeText: isOptionSymbol
        ? ''
        : (live.isExtended && live.closePrice > 0 ? `Close ${fmtCurrency(live.closePrice)}` : ''),
      bidText: optionBidText,
      askText: optionAskText,
      isUp: dayChangeDollar >= 0,
      isExtended: live.isExtended,
    }
  })() : null
  const nonActiveQuotePrice = optionHeadlinePrice ?? nonActiveCurrentPrice
  const nonActiveQuote = nonActiveQuotePrice != null && nonActiveQuotePrice > 0 ? (() => {
    const displayPrice = nonActiveQuotePrice
    const quoteSession = effectiveIbkrQuoteSession || marketSession
    const presentation = quoteSessionPresentation(quoteSession)
    const isExtended = presentation != null
    const closePrice = selectHeadlineChangeReference({
      isExtended,
      isOption: isOptionSymbol,
      sessionClose: effectiveIbkrSessionClose,
      lastRth: effectiveIbkrLastRth,
      previousClose: effectiveIbkrClose,
    })
    const dayChangeDollar = closePrice != null ? displayPrice - closePrice : null
    const dayChangePct = closePrice != null ? (dayChangeDollar / closePrice) * 100 : null
    const isUp = dayChangeDollar != null ? dayChangeDollar >= 0 : true
    return {
      sessionLabel: presentation?.label || '',
      sessionTextClass: presentation?.textClass || '',
      price: fmtCurrency(displayPrice),
      changeText: dayChangeDollar != null
        ? `${fmtSigned(dayChangeDollar)} (${dayChangePct >= 0 ? '+' : ''}${dayChangePct.toFixed(2)}%)`
        : '',
      closeText: isOptionSymbol
        ? ''
        : (isExtended && closePrice != null && closePrice > 0 ? `Close ${fmtCurrency(closePrice)}` : ''),
      bidText: optionBidText,
      askText: optionAskText,
      isUp,
      isExtended,
    }
  })() : null
  const legacyQuote = activeQuote ?? nonActiveQuote ?? null
  const quote = equityDisplay ? (() => {
    const { price: displayPrice, change: amount, changePercent, presentation, fallbackToRegular, isUp } = equityHeadline(equityDisplay)
    return {
      sessionLabel: fallbackToRegular ? '' : [presentation?.label, equityDisplay.freshness !== 'live' ? equityDisplay.freshness : ''].filter(Boolean).join(' · '),
      sessionTextClass: presentation?.textClass || '',
      price: fmtCurrency(displayPrice),
      changeText: amount != null ? `${fmtSigned(amount)} (${fmtPct(changePercent)})` : '',
      closeText: presentation && equityDisplay.closePrice != null ? `Close ${fmtCurrency(equityDisplay.closePrice)}` : '',
      bidText: '', askText: '', isUp, isExtended: Boolean(presentation),
    }
  })() : legacyQuote
  const activeHeadlinePnl = isActive && live ? (() => {
    if (live.usesLotAccounting) {
      return {
        label: 'Open P&L',
        value: live.openPnl,
        pct: live.openPnlPct,
      }
    }
    return {
      label: 'Unrealized P&L',
      value: live.unrealizedPnl,
      pct: live.unrealizedPct,
    }
  })() : null
  let mobilePnlLabel = 'P&L'
  let mobilePnlValue = METRIC_PLACEHOLDER
  let mobilePnlColor = 'text-inkMuted'
  let mobileMetrics = []

  if (isActive && live) {
    const pnlValue = activeHeadlinePnl.value
    const pnlPct = activeHeadlinePnl.pct
    mobilePnlLabel = activeHeadlinePnl.label
    mobilePnlValue = (
      <PrivateValue>
        {fmtSigned(pnlValue)} <span className="text-[10px]">({fmtPct(pnlPct)})</span>
      </PrivateValue>
    )
    mobilePnlColor = clr(pnlValue)
    mobileMetrics = [
      { label: positions[0]?.instrument_type === 'OPTION' ? 'Contracts' : 'Total Shares', value: fmtQty(live.totalQty) },
      {
        label: live.usesLotAccounting ? 'Remaining Basis' : 'Cost Basis',
        value: (
          <PrivateValue>
            {fmtCurrency(live.totalCostBasis)}
            {live.avgEntryPrice > 0 && <span className="text-inkMuted ml-1">({fmtCurrency(live.avgEntryPrice)})</span>}
          </PrivateValue>
        ),
      },
      // The compact card already shows the headline P&L, so the expanded
      // mobile metrics show the complementary lot-accounting value.
      {
        label: mobilePnlLabel === 'Campaign P&L' ? 'Open P&L' : 'Campaign P&L',
        value: (
          <PrivateValue>
            {fmtSigned(mobilePnlLabel === 'Campaign P&L' ? live.openPnl : live.campaignPnl)}
          </PrivateValue>
        ),
        colorClass: clr(mobilePnlLabel === 'Campaign P&L' ? live.openPnl : live.campaignPnl),
      },
      {
        label: 'Risk',
        value: live.totalRisk != null ? <PrivateValue>{fmtSigned(live.stopNetExposure)}</PrivateValue> : METRIC_PLACEHOLDER,
        colorClass: live.totalRisk != null ? clr(live.stopNetExposure) : 'text-inkMuted',
      },
      {
        label: 'R-Multiple',
        value: live.rMultiple != null ? `${live.rMultiple >= 0 ? '+' : ''}${live.rMultiple.toFixed(2)}R` : METRIC_PLACEHOLDER,
        colorClass: live.rMultiple != null ? clr(live.rMultiple) : 'text-inkMuted',
      },
      { label: 'Days in Trade', value: live.daysInTrade != null ? `${live.daysInTrade}d` : METRIC_PLACEHOLDER },
    ]
  } else if (isClosed && closed) {
    mobilePnlLabel = 'Total P&L'
    mobilePnlValue = (
      <PrivateValue>
        {fmtSigned(closed.totalNetPnl)}
        {closed.pnlPct != null && <span className="text-[10px]"> ({fmtPct(closed.pnlPct)})</span>}
      </PrivateValue>
    )
    mobilePnlColor = clr(closed.totalNetPnl)
    mobileMetrics = [
      { label: 'Entry', value: <PrivateValue>{fmtCurrency(closed.avgEntry)}</PrivateValue> },
      { label: 'Exit', value: <PrivateValue>{fmtCurrency(closed.avgExit)}</PrivateValue> },
      { label: mobilePnlLabel, value: mobilePnlValue, colorClass: mobilePnlColor },
      {
        label: 'Risk $',
        value: closed.totalRisk > 0 ? <PrivateValue>{fmtCurrency(closed.totalRisk)}</PrivateValue> : METRIC_PLACEHOLDER,
        colorClass: closed.totalRisk > 0 ? 'text-ember' : 'text-inkMuted',
      },
      {
        label: 'R-Multiple',
        value: closed.rMultiple != null ? `${closed.rMultiple >= 0 ? '+' : ''}${closed.rMultiple.toFixed(2)}R` : METRIC_PLACEHOLDER,
        colorClass: closed.rMultiple != null ? clr(closed.rMultiple) : 'text-inkMuted',
      },
      { label: 'Result', value: closed.result },
    ]
  } else if (!isActive && !isClosed && staticData) {
    mobilePnlLabel = staticData.totalPnl != null ? 'Total P&L' : 'Max Loss'
    mobilePnlValue = (
      <PrivateValue>
        {staticData.totalPnl != null
          ? fmtSigned(staticData.totalPnl)
          : (staticData.totalMaxLoss !== 0 ? fmtCurrency(staticData.totalMaxLoss) : METRIC_PLACEHOLDER)}
      </PrivateValue>
    )
    mobilePnlColor = staticData.totalPnl != null ? clr(staticData.totalPnl) : (staticData.totalMaxLoss !== 0 ? 'text-ember' : 'text-inkMuted')
    mobileMetrics = [
      { label: isGrouped ? 'Avg Entry' : 'Entry Price', value: <PrivateValue>{staticData.entryPrice != null ? fmtCurrency(staticData.entryPrice) : METRIC_PLACEHOLDER}</PrivateValue> },
      { label: 'Stop Price', value: <PrivateValue>{staticData.stopPrice != null ? fmtCurrency(staticData.stopPrice) : METRIC_PLACEHOLDER}</PrivateValue>, colorClass: 'text-ember' },
      { label: 'Target Price', value: <PrivateValue>{staticData.targetPrice != null ? fmtCurrency(staticData.targetPrice) : METRIC_PLACEHOLDER}</PrivateValue>, colorClass: 'text-forest' },
      { label: isOCCSymbol(symbol) ? 'Contracts' : 'Total Shares', value: <PrivateValue>{staticData.totalShares > 0 ? Math.round(staticData.totalShares) : METRIC_PLACEHOLDER}</PrivateValue> },
      { label: mobilePnlLabel, value: mobilePnlValue, colorClass: mobilePnlColor },
      { label: 'Stage', value: stageLabel },
    ]
  } else {
    mobileMetrics = [
      { label: isGrouped ? 'Avg Entry' : 'Entry Price', value: METRIC_PLACEHOLDER },
      { label: 'Stop Price', value: METRIC_PLACEHOLDER, colorClass: 'text-ember' },
      { label: 'Target Price', value: METRIC_PLACEHOLDER, colorClass: 'text-forest' },
      { label: isOCCSymbol(symbol) ? 'Contracts' : 'Total Shares', value: METRIC_PLACEHOLDER },
      { label: mobilePnlLabel, value: mobilePnlValue, colorClass: mobilePnlColor },
      { label: 'Stage', value: stageLabel },
    ]
  }

  const toggleMobileMetrics = (event) => {
    if (event.target?.closest?.('button,a,input,textarea,select,[role="button"]')) return
    if (!isMobileHeaderLayout || positionList) return
    setMobileMetricsOpen((open) => !open)
  }

  const ProtectionStatusBody = ({ compact = false } = {}) => {
    if (compact) {
      const rowClass = 'flex items-center gap-1.5 min-w-0'
      const textClass = 'font-mono text-[10px] leading-tight truncate'
      if (isActive && wsOrdersReady && protection?.unprotected > 0) {
        return (
          <div data-testid="protection-status-body" className={`${rowClass} justify-between`}>
            <div className="flex items-center gap-1.5 min-w-0">
              <AlertTriangle size={12} className="text-ember shrink-0" />
              <span className={`${textClass} text-ember`}>{protection.unprotected} unprotected</span>
            </div>
            <button
              onClick={onPlaceStop}
              className="font-mono text-[8px] font-semibold px-1.5 py-0.5 rounded bg-ember text-white uppercase tracking-wider shrink-0"
            >
              Stop
            </button>
          </div>
        )
      }
      if (isActive && wsOrdersReady) {
        return (
          <div data-testid="protection-status-body" className={rowClass}>
            <div className="w-2 h-2 rounded-full bg-forest shrink-0" />
            <span className={`${textClass} text-forest/80`}>All shares protected</span>
          </div>
        )
      }
      if (isActive) {
        return (
          <div data-testid="protection-status-body" className={rowClass}>
            <div className="w-2 h-2 rounded-full bg-inkMuted/70 shrink-0" />
            <span className={`${textClass} text-inkMuted`}>Checking protection...</span>
          </div>
        )
      }
      if (isClosed) {
        return (
          <div data-testid="protection-status-body" className={rowClass}>
            <div className="w-2 h-2 rounded-full bg-inkMuted shrink-0" />
            <span className={`${textClass} text-inkMuted`}>Position closed</span>
          </div>
        )
      }
      return (
        <div data-testid="protection-status-body" className={rowClass}>
          <div className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
          <span className={`${textClass} text-blue-400/85`}>{workspaceStatusLabel}</span>
        </div>
      )
    }

    return (
      <div
        data-testid="protection-status-body"
        className="grid grid-cols-[16px_minmax(0,1fr)_96px] items-center gap-2 min-h-[22px] lg:min-h-[32px]"
        style={{ contain: 'layout paint' }}
      >
        {isActive && wsOrdersReady && protection?.unprotected > 0 ? (
          <>
            <AlertTriangle size={14} className="text-ember shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[11px] font-medium text-ember truncate">
                {protection.unprotected} {protection.unprotected === 1 ? 'share' : 'shares'} unprotected
              </div>
              <div className="font-mono text-[9px] text-inkMuted truncate">
                Stops cover {protection.totalProtected} of {protection.totalHeld} shares
              </div>
            </div>
            <button
              onClick={onPlaceStop}
              className="font-mono text-[9px] font-semibold px-2.5 py-1 rounded bg-ember hover:bg-ember/80 text-white transition-colors shrink-0 uppercase tracking-wider"
            >
              Place Stop
            </button>
          </>
        ) : isActive && wsOrdersReady ? (
          <>
            <div className="w-2 h-2 rounded-full bg-forest shrink-0" />
            <span className="font-mono text-[11px] text-forest/80 truncate">All shares protected</span>
            <div aria-hidden="true" />
          </>
        ) : isActive ? (
          <>
            <div className="w-2 h-2 rounded-full bg-inkMuted/70 shrink-0" />
            <span className="font-mono text-[10px] text-inkMuted truncate">Checking protection...</span>
            <div aria-hidden="true" />
          </>
        ) : isClosed ? (
          <>
            <div className="w-2 h-2 rounded-full bg-inkMuted shrink-0" />
            <span className="font-mono text-[11px] text-inkMuted truncate">Position closed</span>
            <div aria-hidden="true" />
          </>
        ) : (
          <>
            <div className="w-2 h-2 rounded-full bg-blue-400 shrink-0" />
            <span className="font-mono text-[11px] text-blue-400/85 truncate">{workspaceStatusLabel}</span>
            <div aria-hidden="true" />
          </>
        )}
      </div>
    )
  }

  const PnlMetricWithBreakdown = ({ label, value, pct, colorClass }) => (
    <div
      ref={pnlBreakdown.triggerRef}
      className="relative min-w-0 cursor-pointer"
      onMouseEnter={pnlBreakdown.openDropdown}
      onMouseLeave={pnlBreakdown.close}
      onClick={pnlBreakdown.toggle}
    >
      <Metric testId="position-header-metric-pnl" label={label} colorClass={colorClass}>
        <PrivateValue>
          {fmtSigned(value)}{' '}
          <span className="text-xs">({fmtPct(pct)})</span>
        </PrivateValue>
      </Metric>
      {pnlBreakdown.open && (
        <div className="pointer-events-none absolute top-full left-0 mt-1 z-50 bg-canvas border border-border rounded-lg shadow-lg p-2 min-w-[230px]">
          <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-1.5">P&L Breakdown</div>
          <div className="space-y-1">
            <div className="flex items-center justify-between gap-4">
              <span className="font-mono text-[10px] text-inkMuted">Campaign P&L</span>
              <span className={`font-mono text-[10px] tabular-nums ${clr(live.campaignPnl)}`}>
                <PrivateValue>{fmtSigned(live.campaignPnl)}</PrivateValue>
              </span>
            </div>
            {label !== 'Open P&L' && live.usesLotAccounting && live.openPnl != null && (
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-[10px] text-inkMuted">Open P&L</span>
                <span className={`font-mono text-[10px] tabular-nums ${clr(live.openPnl)}`}>
                  <PrivateValue>{fmtSigned(live.openPnl)}</PrivateValue>
                </span>
              </div>
            )}
            {live.realizedPnl != null && (
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-[10px] text-inkMuted">Realized P&L</span>
                <span className={`font-mono text-[10px] tabular-nums ${clr(live.realizedPnl)}`}>
                  <PrivateValue>{fmtSigned(live.realizedPnl)}</PrivateValue>
                </span>
              </div>
            )}
            {live.brokerOpenPnl != null && (
              <div className="flex items-center justify-between gap-4">
                <span className="font-mono text-[10px] text-inkMuted">Broker Open P&L</span>
                <span className={`font-mono text-[10px] tabular-nums ${clr(live.brokerOpenPnl)}`}>
                  <PrivateValue>{fmtSigned(live.brokerOpenPnl)}</PrivateValue>
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )

  if (ledger) return <>
    <MarketWidgetHeader />
    {ledgerTarget && createPortal(<PositionLedgerPager userId={user?.id} symbol={symbol} stage={stage} live={isActive ? live : null} loading={isActive && loading}
      refreshVersion={brokerRefreshVersion} />, ledgerTarget)}
  </>

  return (
    // `relative` anchors the mobile metrics overlay. The dropdown covers the
    // status card/chart instead of growing the header stack.
    <div className="relative flex flex-col lg:flex-row gap-2 lg:gap-1 items-stretch">
      {/* LEFT BOX: Position & Asset Info */}
      <div
        data-testid="position-header-left-card"
        className={positionList ? 'w-full lg:flex-[3] min-w-0 min-h-[104px] lg:min-h-[100px] justify-center bg-canvas border border-border rounded-lg px-3 py-2 flex flex-col gap-2' : 'w-full lg:w-1/2 min-w-0 min-h-[104px] lg:min-h-[86px] bg-canvas border border-border rounded-lg px-2 py-2 flex flex-col justify-center gap-1.5'}
        onClick={toggleMobileMetrics}
      >
        {/* Row 1: Symbol + badges + price + account pills */}
        <div data-testid="position-header-row" className={positionList ? 'flex items-center gap-3 min-w-0' : HEADER_ROW_CLASS}>
          {!positionList && <><h1 className="font-black text-xl md:text-2xl uppercase tracking-wide text-ink leading-none shrink-0">{displaySymbol}</h1>
          {isMobileHeaderLayout && quote && <MobileInlineQuote {...quote} />}
          {!isMobileHeaderLayout && <QuoteRow {...(quote ?? {})} />}</>}
          {positionList && <h2 className="font-sans text-xs font-semibold text-ink">Position</h2>}

          <div className="flex items-center justify-end gap-2 shrink-0 ml-auto">
            {(isActive || isClosed || hasSetup) && (() => {
              const headerOcc = isOCCSymbol(symbol) ? parseOCC(symbol) : null
              const isHeaderBullish = headerOcc
                ? (side === 'LONG' ? headerOcc.right === 'C' : headerOcc.right === 'P')
                : side !== 'SHORT'
              return isHeaderBullish ? (
                <svg width="20" height="20" viewBox="-5 -10 110 110" xmlns="http://www.w3.org/2000/svg" className="text-forest shrink-0">
                  <g transform="translate(100, 0) scale(-1, 1)" fill="currentColor" fillOpacity="0.75">
                    <path d="m65.105 28.586c4.5977-8.7617-1.6016-10.77-11.078-13.613-7.2695-2.1797-14.145-7.0547-17.445-11.848 0.35547 11.434 11.938 16.586 18.859 20.16 5.4609 2.8203 0.33594 6.5781-3.8281 7.3633 0.27344 1.3359 1.3555 2.5742 2.457 3.1641-3.4766-0.58594-4.7344-3.5898-4.4219-5.8945 0.39062-2.8711 3.1445-2.3789 5.0898-4.0078-6.0508-2.5938-12.984 0.66406-15.836 3.5273 0.070312-0.74219 0.45312-1.625 1.2188-2.1523-8.0742-2.1211-10.688 6.5117-11.227 7.6641-0.011719-1.7305 0.71875-4.5781 1.8281-5.8164-0.36328 0.003907-0.74219 0.007813-1.1367 0.007813-7.4805 0-19.969-1.1992-18.637-19.418-8.0234 11.078-2.8359 25.812 16.199 28.172-4.9531 1.1797-6.8359 3.0664-8.0156 4.2422 1.4141 2.1211 3.5938 2.8008 3.5938 2.8008-0.91406 1.0898 0.058594 3.7422 0.64844 4.668-2.4102 5.457-6.4883 10.746-9.9609 14.695-2.1523 2.4453 0.023438 3.8047 1.207 6.0117 1.1172 2.0781 0.73438 3.6523 3.5664 4.2812 0.91406 2.6758 4.7773 3.4766 7.5977 3.4766 0.82422 0 1.5586-0.066406 2.1055-0.17578 3.5352-0.70703 4.5703-3.6133 7.8203-3.6133 2.5938 0 9.4141 2.8672 11.496 6.125 2.2656 3.5352 4.6094 15.41 8.2266 18.473 17.918-6.1289 37.25-33.164 37.25-49.664-12.254-15.562-21.918-17.215-27.578-18.629zm-37.012 43.574c-2.2969 0-5.8125-2.2383-10.266-1.3945-0.53125-0.23438-0.66406-0.59375-0.98438-1.4609-0.18359-0.49609-0.41016-1.1133-0.76562-1.7734-0.32812-0.61328-0.71094-1.1523-1.0469-1.625-1.0898-1.5391-1.1094-1.6758-0.375-2.5156 0.44141-0.50391 0.73047-0.83594 1.1484-1.3359 4.8594 1.0312 5.7305-1.4844 8.082-1.4844 2.7109 0 3.418 2.8281 2.3594 4.6758 0-1.6914-1.1016-2.9492-2.2383-3.0664-0.13672 2.1602-2.9453 2.6719-3.5352 2.7891 0 3.5742 2.1211 4.3594 3.9297 4.9102 1.8086 0.55078 5.7344 1.9648 8.6055-1.7305-0.003906 1.4961-2.4609 4.0117-4.9141 4.0117zm-5.7539-5.0234c1.0898-0.14844 2.3867-1.1992 2.5664-2.5625 0.46875 1.1172 0.23438 3.3594-2.5664 2.5625zm3.3555-9.9844c0.55078-2.082 3.2812-6.9727 5.8945-9.2734 1.3047-0.46094 2.3945 0.23438 2.3945 0.23438-2.0508 1.5273-6.4414 6.3281-8.2891 9.0391zm5.0703-12.262c-0.45312-1.2188-0.98438-1.707-1.5234-2.3359 0.70703-2.2695 2.5781-5.457 5.1602-6.8711-2.082 2.3164-3.6367 5.8477-3.6367 9.207zm3.8516 0.66797c2.1992-1.8867 4.6367-9.2344 10.805-5.3047-5.6367-0.44531-6.6016 5.3047-10.805 5.3047zm4.4688-0.32422c0.61719-1.3555 1.7695-2.1797 3.2422-2.5352 0.32422 2.832-1.5312 3.168-3.2422 2.5352zm6.0039-2.3555c0.82422 5.3633-4.7383 5.3633-6.5664 3.918 6.1602-0.20703 4.3047-3.2109 6.5664-3.918zm-4.0898 24.484c11.848-1.9453 16.293-7.2969 17.328-17.184 2.5938 12.945-8.7812 19.133-17.328 17.184zm18.211 8.668c2.3594-3.5352 6.543-14.91 4.5977-25.109 5.543 9.3125 1.0039 20.336-4.5977 25.109zm0.11328-34.355c0.15625-0.74219 1.6055-2.1445 2.4844-2.3008-1.0039-0.47266-2.6484-0.30078-3.7109 0 1.207-1.8281 5.4805-3.7109 8.957-3.9492-5.3633-1.3281-8.543 0.30859-10.746 2.1875 1.2461-2.3516 4.4375-6.7734 14.281-2.7773-2.2773 5.375-7.5352 7.7461-11.266 6.8398z" fillRule="evenodd"/>
                  </g>
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="-5 -10 110 110" xmlns="http://www.w3.org/2000/svg" className="text-ember shrink-0">
                  <g fill="currentColor" fillOpacity="0.75">
                    <path d="m25.074 20.148c-2.1055-5.3477 0.0625-10.484 3.0234-10.738 2.3359-0.20312 4.75 1.8672 7.2734 3.4609-1.9688 0.87891-3.8828 1.9961-5.6445 3.4414-1.7148 1.4023-3.2617 2.6719-4.6523 3.8359zm68.891 32.203c-0.32422-0.97266-2.1055-2.6328-3.2422-2.9961 1.2969 9.9648-5.7539 21.711-20.172 29.695 0.48438-0.73047 2.0664-4.6172 1.8633-7.4922-4.3555 9.8164-20.395 14.922-26.086 22.191-0.56641-3.6445-6.7031-12.68-8.1836-17.984-0.39844 1.2305-0.58594 2.3594-0.3125 4.0312-3.7266-2.5938-4.8711-10.672-7.625-12.699-0.76953-0.56641-2.168-0.64062-3.8516-0.64062-0.58594 0-1.207 0.007813-1.8477 0.007813-4.1055 0-9.0117-0.36328-10.828-5.7695-4.0508-1.1328-5.0234-5.5078-5.5078-7.293-0.48438-1.7812-2.4297-3.3008-2.1055-5.5273 0.32422-2.2266 4.1172-3.5469 10.371-10.27 1.8711-2.0156-0.40625-2.6719-0.40625-5.7539 0-2.7148 4.3359-6.3984 14.422-14.664 6.1406-5.0312 14.293-6.0586 19.832-6.0586 1.3906 0 2.6172 0.066406 3.6055 0.14844-0.46875 0.82812-0.84375 2.0078-1.1758 3.1562 2.7969-3.4219 7.1406-8.1836 12.125-8.1836 5.0234 0 10.047 9.1133 5.2461 18.676 1.0742-0.60938 2.8555-2.5117 3.2969-5.7891 3.9922 2.0469 13.176 8.1758 16.855 15.633-1.0547-0.56641-3.2891-0.94531-5.1836-1.0547 1.457 1.2148 9.2344 6.2383 8.9102 18.637zm-64.16-28.684c1.3711-1.3359 4.25-2.875 5.5703-5.3477-1.2344 0.53125-4.8867 2.3125-5.5703 5.3477zm-11.633 8.0625c0.11328 0.57031 1.2461 1.7031 2.25 1.7617 0.5625 0.035156 1.0156-0.33203 1.1406-0.66797-1.0156-0.62109-1.9453-1.8555-2.25-2.8555-0.64062 0.48438-1.2148 1.3828-1.1406 1.7617zm-5.2266 25.84c-0.82812-2.5195-1.7383-2.4766-2.7656-3.293 0.38281 1.2539 0.99609 2.7773 2.1719 3.8086 0.17578-0.1875 0.38281-0.375 0.59375-0.51563zm2.4336-9.4922c-0.54688-1.8711-3.5039-2.125-5.1055-1.6719 2.957 0.027344 3.8906 1.5664 5.1055 1.6719zm-2.4336 4.0898c0.70703 0.32422 1.4492-0.21094 1.3203-0.6875-0.16406-0.59766-1.1328-0.78906-1.8828-1.4805-0.38672 0.91016-0.14453 1.8438 0.5625 2.168zm25.078 4.5586c0-5.0625-6.4805-7.6172-7.7773-18.758-2.8047 0.50781-6.5234 3.5234-7.6758 4.4141 0.95313-1.6289 4.3594-5.1484 6.6016-6.7031-3.707-1.793-4.8867-3.418-2.25-7.9609-3.0156 2.5508-2.0039 4.7695-3.2812 5.832-4.6602 3.8828-9.8477 8.8594-12.398 11.145 2.3164-0.11719 5.5352 0.09375 6.3867 3.0156 0.39453-0.0625 1.0117-0.40625 1.457-0.85156-0.16797 2.4609-2.3281 6.082-5.8008 7.8945 0 0.003906 0 0.003906 0.003906 0.007813 0.003907 1.0625 0.23828 2.0664 1.1797 2.6445l0.003906 0.003906c2.6914 0.16797 7.2656 2.1016 9.457 1.9102 2.7852-0.24219 6.9258-4.1328 9.3594-4.1328 2.2695 0 2.5938 2.2266 1.8633 2.6719-0.73047 0.44531-1.2148-0.66797-3.2422 0.12109-2.8867 1.125-4.457 2.9453-7.3242 3.2188-2.7227 0.26562-4.7891-0.87109-8.7891-0.62109 1.0508 1.3906 2.8828 2.9492 6.5117 2.9492 5.5078 0 6.8477-2.0664 10.086-2.3516 1.4492-0.12109 5.6289-0.68359 5.6289-4.4492zm-0.86328-23.266c-1.4883-0.82031-2.582 1.5195-3.918 2.0664 1.5508 1.2148 4.2852 0.69922 3.918-2.0664zm5.8047-0.51563c-0.50781-1.6797-3.3633-3.4648-5.5078-3.5664 0.60938-0.6875 1.3359-1.1328 2.3906-1.1328-5.3672-1.7812-7.9414 3.3008-9.1953 3.5469-0.83594 0.16016-1.0117-0.91016-0.40625-2.0469-1.5 1.5781-1.7812 3.8477 0 4.1719s3.2109-2.25 5.9961-2.7891c2.6797-0.52344 4.9297 0.44922 6.7227 1.8164zm13.449-15.797c8.5469-10.531 12.68-2.3906 9.5195 5.9141 4.5781-4.9023 2.5312-11.875-1.1328-12.719-4.2148-0.97656-8.0234 4.4922-8.3867 6.8047zm0.48438 1.6602c1.2344 1.7422 3.3711 3.1289 5.1836 3.5234-1.4492-1.1562-2.6328-2.2695-2.6719-2.7539 0.28516-0.52734 0.98438-0.92969 1.6797-1.1133-1.1719-0.28516-3.4805-0.125-4.1914 0.34375zm9.6445 29.57c0.082031 7.4922-2.918 12.598-5.0625 15.109 0.023437-0.78516-0.042969-1.9062-0.36328-2.875-0.48438 3.1172-1.6602 5.9414-3.3203 7.9414 3.7656-1.3789 9.3594-8.9531 9.5195-13.855 0.44531 0.60938 0.91016 1.8242 0.97266 3.1602 0.88281-3.8906-0.12891-7.6562-1.7461-9.4805zm-0.77344-10.207c2.3516 3.1992 4.4141 9.3984 4.7812 15.434 2.1055-6.8047-1.2969-13.207-4.7812-15.434z" fillRule="evenodd"/>
                  </g>
                </svg>
              )
            })()}

            <span className={`font-mono text-[9px] px-2 py-0.5 rounded-full border uppercase tracking-widest flex items-center gap-1.5 ${badgeCls}`}>
              {isActive && (
                <span className="relative flex h-2 w-2">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-forest opacity-50" />
                  <span className="relative inline-flex rounded-full h-2 w-2 bg-forest" />
                </span>
              )}
              {emptyWorkspace ? 'No setup' : stage}
            </span>

            {!hasSetup && primary.setup_type && (
              <span className="font-mono text-[9px] px-2 py-0.5 rounded-full border bg-canvas2 text-inkMuted border-border">
                {primary.setup_type}
              </span>
            )}
          </div>

        </div>

        {isMobileHeaderLayout && !positionList && (
        <div className="lg:hidden grid grid-cols-2 gap-2">
          <MobileMetric testId="position-header-mobile-pnl-card" label={mobilePnlLabel} colorClass={mobilePnlColor}>
            {mobilePnlValue}
          </MobileMetric>
          <MobileMetric testId="position-header-status-card" label="Status" valueClassName="min-w-0">
            {ProtectionStatusBody({ compact: true })}
          </MobileMetric>
        </div>
        )}

        {/* Metrics — flush under Row 1 */}
        {isActive && loading && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <MetricSkeleton testId="position-header-metric-shares" />
            <MetricSkeleton testId="position-header-metric-basis" />
            <MetricSkeleton testId="position-header-metric-pnl" />
            <MetricSkeleton testId="position-header-metric-risk" />
            <MetricSkeleton testId="position-header-metric-r-multiple" />
            <MetricSkeleton testId="position-header-metric-days" />
          </div>
        )}

        {isActive && !loading && !live && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <Metric testId="position-header-metric-shares" label="Total Shares" />
            <Metric testId="position-header-metric-basis" label="Cost Basis" />
            <Metric testId="position-header-metric-pnl" label="Open P&L" />
            <Metric testId="position-header-metric-risk" label="Risk" />
            <Metric testId="position-header-metric-r-multiple" label="R-Multiple" />
            <Metric testId="position-header-metric-days" label="Days in Trade" />
          </div>
        )}

        {isActive && live && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <div
              ref={sharesBreakdown.triggerRef}
              className="relative min-w-0 cursor-pointer"
              onMouseEnter={sharesBreakdown.openDropdown}
              onMouseLeave={sharesBreakdown.close}
              onClick={sharesBreakdown.toggle}
            >
              <Metric testId="position-header-metric-shares" label={positions[0]?.instrument_type === 'OPTION' ? 'Contracts' : 'Total Shares'}>
                {fmtQty(live.totalQty)}
              </Metric>
              {sharesBreakdown.open && live.accounts.length > 0 && (
                <div className="pointer-events-none absolute top-full left-0 mt-1 z-50 bg-canvas border border-border rounded-lg shadow-lg p-2 min-w-[200px]">
                  <div className="font-mono text-[9px] tracking-widest uppercase text-inkMuted mb-1.5">Account Breakdown</div>
                  {live.accounts.map((a, i) => {
                    const pct = live.totalQty > 0 ? ((a.qty / live.totalQty) * 100).toFixed(1) : '0.0'
                    return (
                      <div key={i} className="flex items-center justify-between gap-4 py-0.5">
                        <AccountName accountNumber={a.accountNumber} className="font-mono text-[10px] text-ink" />
                        <div className="font-mono text-[10px] tabular-nums text-ink">
                          <PrivateValue>{fmtQty(a.qty)} <span className="text-inkMuted">({pct}%)</span></PrivateValue>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
            <Metric testId="position-header-metric-basis" label={live.usesLotAccounting ? 'Remaining Basis' : 'Cost Basis'}>
              <PrivateValue>
                {fmtCurrency(live.totalCostBasis)}
                {live.avgEntryPrice > 0 && (
                  <span className="text-inkMuted ml-1">({fmtCurrency(live.avgEntryPrice)})</span>
                )}
              </PrivateValue>
            </Metric>
            <PnlMetricWithBreakdown
              label={activeHeadlinePnl.label}
              value={activeHeadlinePnl.value}
              pct={activeHeadlinePnl.pct}
              colorClass={clr(activeHeadlinePnl.value)}
            />
            <div
              ref={riskBreakdown.triggerRef}
              className={`relative min-w-0 ${live.totalRisk != null ? 'cursor-pointer' : ''}`}
              onMouseEnter={riskBreakdown.openDropdown}
              onMouseLeave={riskBreakdown.close}
              onClick={riskBreakdown.toggle}
            >
              <Metric testId="position-header-metric-risk" label="Risk" colorClass={live.totalRisk != null ? clr(live.stopNetExposure) : 'text-inkMuted'}>
                {live.totalRisk != null ? <PrivateValue>{fmtSigned(live.stopNetExposure)}</PrivateValue> : METRIC_PLACEHOLDER}
              </Metric>
              {riskBreakdown.open && live.totalRisk != null && (
                <div className="pointer-events-none absolute top-full left-0 mt-1 z-50 bg-canvas border border-border rounded-lg shadow-lg p-3 min-w-[300px]">
                  <div className="font-mono text-[10px] tracking-widest uppercase text-inkMuted mb-2">Risk Breakdown</div>
                  <div className="space-y-1.5">
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Stop Exposure</span>
                      <span className={`font-mono text-[10px] tabular-nums ${clr(live.stopNetExposure)}`}>
                        <PrivateValue>{fmtSigned(live.stopNetExposure)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Stop Exposure %</span>
                      <span className={`font-mono text-[10px] tabular-nums ${clr(live.stopExposurePct)}`}>
                        <PrivateValue>{fmtPct(live.stopExposurePct)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Open Position Risk</span>
                      <span className={`font-mono text-[10px] tabular-nums ${clr(live.openPositionRisk)}`}>
                        <PrivateValue>{fmtSigned(live.openPositionRisk)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Open Position Risk %</span>
                      <span className={`font-mono text-[10px] tabular-nums ${clr(live.openPositionRiskPct)}`}>
                        <PrivateValue>{fmtPct(live.openPositionRiskPct)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Initial Risk</span>
                      <span className="font-mono text-[10px] tabular-nums text-ember">
                        <PrivateValue>{fmtLossCurrency(live.initialRisk)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Position Starting Capital at Risk %</span>
                      <span className="font-mono text-[10px] tabular-nums text-ember">
                        <PrivateValue>{fmtLossPct(live.startingCapitalRiskPct)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Open Portfolio Risk %</span>
                      <span className={`font-mono text-[10px] tabular-nums ${clr(live.openPortfolioRiskPct)}`}>
                        <PrivateValue>{fmtPct(live.openPortfolioRiskPct)}</PrivateValue>
                      </span>
                    </div>
                    <div className="flex items-center justify-between gap-4">
                      <span className="font-mono text-[10px] text-inkMuted">Portfolio Starting Capital at Risk %</span>
                      <span className="font-mono text-[10px] tabular-nums text-ember">
                        <PrivateValue>{fmtLossPct(live.portfolioStartingRiskPct)}</PrivateValue>
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <Metric testId="position-header-metric-r-multiple" label="R-Multiple" colorClass={live.rMultiple != null ? clr(live.rMultiple) : 'text-inkMuted'}>
              {live.rMultiple != null ? `${live.rMultiple >= 0 ? '+' : ''}${live.rMultiple.toFixed(2)}R` : METRIC_PLACEHOLDER}
            </Metric>
            <Metric testId="position-header-metric-days" label="Days in Trade">
              {live.daysInTrade != null ? `${live.daysInTrade}d` : METRIC_PLACEHOLDER}
            </Metric>
          </div>
        )}

        {isClosed && closed && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <Metric testId="position-header-metric-entry" label="Entry"><PrivateValue>{fmtCurrency(closed.avgEntry)}</PrivateValue></Metric>
            <Metric testId="position-header-metric-exit" label="Exit"><PrivateValue>{fmtCurrency(closed.avgExit)}</PrivateValue></Metric>
            <Metric testId="position-header-metric-pnl" label="Total P&L" colorClass={clr(closed.totalNetPnl)}>
              <PrivateValue>
                {fmtSigned(closed.totalNetPnl)}{' '}
                {closed.pnlPct != null && <span className="text-xs">({fmtPct(closed.pnlPct)})</span>}
              </PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-risk" label="Risk $" colorClass={closed.totalRisk > 0 ? 'text-ember' : 'text-inkMuted'}>
              {closed.totalRisk > 0 ? <PrivateValue>{fmtCurrency(closed.totalRisk)}</PrivateValue> : METRIC_PLACEHOLDER}
            </Metric>
            <Metric testId="position-header-metric-r-multiple" label="R-Multiple" colorClass={closed.rMultiple != null ? clr(closed.rMultiple) : 'text-inkMuted'}>
              {closed.rMultiple != null ? `${closed.rMultiple >= 0 ? '+' : ''}${closed.rMultiple.toFixed(2)}R` : METRIC_PLACEHOLDER}
            </Metric>
            <Metric testId="position-header-metric-result" label="Result">
              <span className={`font-mono text-[10px] px-2 py-0.5 rounded-full font-semibold border ${
                closed.result === 'WIN' ? 'bg-forest/10 text-forest border-forest/30' :
                closed.result === 'LOSS' ? 'bg-ember/10 text-ember border-ember/30' :
                'bg-canvas2 text-inkMuted border-border'
              }`}>
                {closed.result}
              </span>
            </Metric>
          </div>
        )}

        {!isActive && !isClosed && staticData && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <Metric testId="position-header-metric-entry" label={isGrouped ? 'Avg Entry' : 'Entry Price'}>
              <PrivateValue>{staticData.entryPrice != null ? fmtCurrency(staticData.entryPrice) : '\u2014'}</PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-stop" label="Stop Price" colorClass="text-ember">
              <PrivateValue>{staticData.stopPrice != null ? fmtCurrency(staticData.stopPrice) : '\u2014'}</PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-target" label="Target Price" colorClass="text-forest">
              <PrivateValue>{staticData.targetPrice != null ? fmtCurrency(staticData.targetPrice) : '\u2014'}</PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-shares" label={isOCCSymbol(symbol) ? 'Contracts' : 'Total Shares'}>
              <PrivateValue>{staticData.totalShares > 0 ? Math.round(staticData.totalShares) : METRIC_PLACEHOLDER}</PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-pnl" label={staticData.totalPnl != null ? 'Total P&L' : 'Max Loss'} colorClass={staticData.totalPnl != null ? clr(staticData.totalPnl) : (staticData.totalMaxLoss !== 0 ? 'text-ember' : 'text-inkMuted')}>
              <PrivateValue>
                {staticData.totalPnl != null
                  ? fmtSigned(staticData.totalPnl)
                  : (staticData.totalMaxLoss !== 0 ? fmtCurrency(staticData.totalMaxLoss) : METRIC_PLACEHOLDER)}
              </PrivateValue>
            </Metric>
            <Metric testId="position-header-metric-stage" label="Stage">{stageLabel}</Metric>
          </div>
        )}

        {!isActive && !isClosed && !staticData && (
          <div data-testid="position-header-metrics" role={positionList ? 'list' : undefined} className={metricGrid}>
            <Metric testId="position-header-metric-entry" label={isGrouped ? 'Avg Entry' : 'Entry Price'} />
            <Metric testId="position-header-metric-stop" label="Stop Price" colorClass="text-ember" />
            <Metric testId="position-header-metric-target" label="Target Price" colorClass="text-forest" />
            <Metric testId="position-header-metric-shares" label={isOCCSymbol(symbol) ? 'Contracts' : 'Total Shares'} />
            <Metric testId="position-header-metric-pnl" label="Max Loss" />
            <Metric testId="position-header-metric-stage" label="Stage">{stageLabel}</Metric>
          </div>
        )}

      </div>

      {/* RIGHT BOX: Status & Alerts (EF-114).
          Replaces the old setup-cards strip; setups now live in their own
          tab in the right panel. The "Place Stop" button switches to the
          Quick Actions tab and opens the inline price input there. */}
      {(!isMobileHeaderLayout || positionList) && (
        <div
          data-testid="position-header-status-card"
          className={positionList ? 'w-full lg:flex-1 min-w-0 min-h-[52px] lg:min-h-[100px] bg-canvas border border-border rounded-lg px-3 py-2 flex flex-col justify-center' : 'w-full lg:w-1/2 min-w-0 min-h-[52px] lg:min-h-[86px] bg-canvas border border-border rounded-lg px-2 py-1.5 lg:py-2 flex flex-col justify-center overflow-hidden'}
          onClick={toggleMobileMetrics}
        >
          <div className="font-mono text-[8px] lg:text-[9px] tracking-widest uppercase text-inkMuted mb-1 lg:mb-1.5">
            Status
          </div>
          {ProtectionStatusBody()}
        </div>
      )}

      {/* Mobile metrics dropdown. Tapping either compact card toggles this
          same overlay; it sits above the chart without growing the header. */}
      {isMobileHeaderLayout && !positionList && mobileMetricsOpen && (
        <div
          data-testid="position-header-mobile-metrics"
          className="lg:hidden absolute top-full left-0 right-0 z-[80] mt-1 grid grid-cols-2 gap-2 p-3 rounded-xl border border-border bg-canvas/95 shadow-xl backdrop-blur-sm"
          onClick={toggleMobileMetrics}
        >
          {mobileMetrics.map((metric) => (
            <MobileMetric key={metric.label} label={metric.label} colorClass={metric.colorClass}>
              {metric.value}
            </MobileMetric>
          ))}
        </div>
      )}
    </div>
  )
}
